import type { ComponentType } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { SignalFilters, SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import CreateSignalModalTracked from "app/pages/sales-intelligence/pages/opportunity-list/signals/containers/CreateSignalModal/CreateSignalModalTracked";
import DeleteSignalModalContainer from "app/pages/sales-intelligence/pages/opportunity-list/signals/containers/DeleteSignalModal/DeleteSignalModalContainer";
import EditSignalModalTracked from "app/pages/sales-intelligence/pages/opportunity-list/signals/containers/EditSignalModal/EditSignalModalTracked";
import { useSignalsManagementViewModel } from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalsManagementViewModel";
import createModifySignalModalViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/signal-modal/factory";
import type CreateViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-management/CreateViewProps";
import type EditViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-management/EditViewProps";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import {
    setManagementDeleteSignalId,
    setManagementEditSignalId,
} from "app/pages/sales-intelligence/sub-modules/signals/store/action-creators";
import {
    selectManagementDeleteSignalId,
    selectManagementEditSignalId,
    selectSignalsSource,
} from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import { findUISignalById } from "app/pages/sales-intelligence/sub-modules/signals/utils/findUISignalById";

import SignalsManagementModalTracked from "../../containers/SignalsManagementModal/SignalsManagementModalTracked";

export type SignalsManagementProps = {
    initialTemplateAlias?: string;
    initialSelectedType?: SignalType;
    isManagementViewOpen: boolean;
    selectedSignalCreateType: SignalType | null;

    createView: ComponentType<CreateViewProps>;
    editView: ComponentType<EditViewProps>;

    onManagementViewToggle(): void;
    onManagementViewClose(): void;
    onSignalCreate(signalType: SignalType | null): void;
    onSignalCreateReset?(): void;
    onDeleteViewClose?(): void;
};

const SignalsManagementContainer = (props: SignalsManagementProps) => {
    const {
        initialTemplateAlias,
        initialSelectedType,
        isManagementViewOpen,
        selectedSignalCreateType,
        createView: CreateViewComponent,
        editView: EditViewComponent,
        onManagementViewToggle,
        onManagementViewClose,
        onSignalCreate,
        onSignalCreateReset,
        onDeleteViewClose,
    } = props;

    const signalsManagementViewModel = useSignalsManagementViewModel();

    const dispatch = useDispatch();
    const signalSource = useSelector(selectSignalsSource);
    const managementEditSignalId = useSelector(selectManagementEditSignalId);
    const managementDeleteSignalId = useSelector(selectManagementDeleteSignalId);

    const selectedEditSignal = managementEditSignalId
        ? findUISignalById(signalsManagementViewModel, managementEditSignalId)
        : null;
    const selectedDeleteSignal = managementDeleteSignalId
        ? findUISignalById(signalsManagementViewModel, managementDeleteSignalId)
        : null;

    const isCreateViewOpen = selectedSignalCreateType !== null;
    const isEditViewOpen = selectedEditSignal !== null;

    const modifySignalPreferredSignalType = selectedSignalCreateType ?? selectedEditSignal?.signalType ?? null;
    const modifySignalViewModel = createModifySignalModalViewModel({
        initialTemplateAlias,
        signalsService,
        preferredSignalType: modifySignalPreferredSignalType,
        signalSource: signalSource,
    });

    function handleSignalEdit<F extends SignalFilters>(signal: Readonly<UISignal<F>>): void {
        dispatch(setManagementEditSignalId(signal.id));
    }

    function handleSignalDelete<F extends SignalFilters>(signal: Readonly<UISignal<F>>): void {
        dispatch(setManagementDeleteSignalId(signal.id));
    }

    function handleCreateViewReturn() {
        onSignalCreate(null);

        if (!isManagementViewOpen) {
            onSignalCreateReset?.();
        }
    }

    function handleCreateViewClose() {
        onSignalCreate(null);

        if (isManagementViewOpen) {
            onManagementViewToggle();
        }
    }

    function handleCreateSuccess() {
        onSignalCreate(null);
    }

    function handleEditViewReturn(): void {
        dispatch(setManagementEditSignalId(null));
    }

    function handleEditViewClose(): void {
        dispatch(setManagementEditSignalId(null));

        if (isManagementViewOpen) {
            onManagementViewToggle();
        }
    }

    function handleDeleteViewClose(): void {
        dispatch(setManagementDeleteSignalId(null));

        if (typeof onDeleteViewClose === "function") {
            onDeleteViewClose();
        }
    }

    return (
        <>
            <SignalsManagementModalTracked
                isOpen={isManagementViewOpen}
                viewModel={signalsManagementViewModel}
                initialSelectedType={initialSelectedType}
                onSignalCreate={onSignalCreate}
                onSignalEdit={handleSignalEdit}
                onSignalDelete={handleSignalDelete}
                onClose={onManagementViewClose}
            />

            {isCreateViewOpen ? (
                <CreateSignalModalTracked
                    isOpen={isCreateViewOpen}
                    selectedSignalCreateType={selectedSignalCreateType}
                    viewModel={modifySignalViewModel}
                    onReturn={handleCreateViewReturn}
                    onClose={handleCreateViewClose}
                >
                    {selectedSignalCreateType !== null ? (
                        <CreateViewComponent
                            viewModel={modifySignalViewModel}
                            onSubmitSuccess={handleCreateSuccess}
                        />
                    ) : null}
                </CreateSignalModalTracked>
            ) : null}

            {isEditViewOpen ? (
                <EditSignalModalTracked
                    isOpen={isEditViewOpen}
                    selectedEditSignal={selectedEditSignal}
                    viewModel={modifySignalViewModel}
                    onReturn={handleEditViewReturn}
                    onClose={handleEditViewClose}
                >
                    {selectedEditSignal !== null ? (
                        <EditViewComponent
                            uiSignal={selectedEditSignal}
                            onSubmitSuccess={handleEditViewClose}
                        />
                    ) : null}
                </EditSignalModalTracked>
            ) : null}

            <DeleteSignalModalContainer
                signal={selectedDeleteSignal}
                onClose={handleDeleteViewClose}
            />
        </>
    );
};

export default SignalsManagementContainer;
